import axios from 'axios';
import { useCallback } from 'react';

import { useToast } from '@/hook/useToast';
import { useInteractiveCallback } from '@/hook/useInteractiveCallback';
import { useRefetchReel } from '@/hook/useRefetchReel';
import { useRefreshReels } from '@/hook/useRefreshReels';
import { useReelMap } from '@/store/library/library.hook';
import { useReelsData } from '@/store/reels/reels.hook';

type UseDeleteReelPayload = {
    // NOTE: Called once a reel is deleted or restored, e.g. to reload the page's reels
    onFinish?: () => void;
};

export function useDeleteReel({ onFinish }: UseDeleteReelPayload = {}) {
    const { showToast, hideToast } = useToast();

    const reelMap = useReelMap();
    const refetchReel = useRefetchReel();
    const refreshReels = useRefreshReels();
    const reelsData = useReelsData();

    // NOTE: The full reel cached in the library store (shown in the flyover) has to be
    //   refetched separately, or it keeps its pre-delete/restore status.
    const refetchCachedReel = useInteractiveCallback((reelId: number) => {
        // TODO: Refactor to use codes not IDs
        const flowEditorReelCode = reelsData.find(
            (reel) => reel.id === reelId,
        )?.code;

        if (flowEditorReelCode) {
            refreshReels([flowEditorReelCode]);
        }

        const cachedReel = Object.values(reelMap).find(
            (reel) => reel.id === reelId,
        );

        if (cachedReel) {
            refetchReel(cachedReel.code);
        }
    });

    const restoreReel = useCallback(
        (reelId: number, reelName: string) => {
            return axios
                .post(route('api.internal.reels.restore', { reelId }))
                .then(() => {
                    hideToast();

                    showToast({
                        autoClose: 5000,
                        variant: 'success',
                        label: `The video "${reelName}" has been restored`,
                    });

                    refetchCachedReel(reelId);
                    onFinish?.();
                })
                .catch(() => {
                    hideToast();

                    showToast({
                        autoClose: 5000,
                        variant: 'danger',
                        label: 'Failed to restore the video reel',
                    });
                });
        },
        [showToast, hideToast, refetchCachedReel, onFinish],
    );

    const deleteReel = useCallback(
        (reelId: number, reelName: string) => {
            return axios
                .delete(route('api.internal.reels.destroy', { reelId }))
                .then(() => {
                    showToast({
                        autoClose: 5000,
                        variant: 'success',
                        label: `The video "${reelName}" has been deleted`,
                        action: {
                            label: 'Undo',
                            href: '#',
                            onClick: (e) => {
                                e.preventDefault();

                                restoreReel(reelId, reelName);
                            },
                        },
                    });

                    refetchCachedReel(reelId);

                    onFinish?.();
                })
                .catch(() => {
                    showToast({
                        autoClose: 5000,
                        variant: 'danger',
                        label: 'Failed to delete the video reel',
                    });
                });
        },
        [showToast, restoreReel, refetchCachedReel, onFinish],
    );

    return { deleteReel, restoreReel };
}
